import React, { Component } from 'react';
import { string, bool, arrayOf, array, func } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import moment from 'moment';
import config from '../../config';
import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import {
  required,
  bookingDatesRequired,
  composeValidators,
  numberBetween,
} from '../../util/validators';
import { START_DATE, END_DATE } from '../../util/dates';
import { propTypes } from '../../util/types';
import {
  Form,
  IconSpinner,
  PrimaryButton,
  FieldDateRangeInput,
  FieldTextInput,
  FieldRadioButton,
} from '../../components';
import EstimatedBreakdownMaybe from './EstimatedBreakdownMaybe';
import { daysBetween } from '../../util/dates';
import { maximumCommonNumberOfSeats } from './utils';
import css from './BookingDatesForm.module.scss';
import { getBookingQtyAtleast1Month, residualInterval } from '../../components/BookingBreakdown/subscription/utils';
import { PaymentOptionEnum } from '../../enums/payment-option.enum';
import { ListingFullPaymentOptionEnum } from '../../enums/listing-full-payment-option.enum';

const identity = v => v;

export class BookingDatesFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { focusedInput: null, minBookingPeriodError: false, maximumNumberOfSeats: null };
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.onFocusedInputChange = this.onFocusedInputChange.bind(this);
    this.handleOnChange = this.handleOnChange.bind(this);
  }

  // Function that can be passed to nested components
  // so that they can notify this component when the
  // focused input changes.
  onFocusedInputChange(focusedInput) {
    this.setState({ focusedInput });
  }

  // In case start or end date for the booking is missing
  // focus on that input, otherwise continue with the
  // default handleSubmit function.
  handleFormSubmit(e) {
    const { startDate, endDate } = e.bookingDates || {};
    const { seats } = e;
    const minBookingPeriod = this.props.minBookingPeriod;
    const bookingPeriodInDays = daysBetween(startDate, endDate);
    const isAtLeastMinBookingPeriod =
      typeof bookingPeriodInDays === 'number' && bookingPeriodInDays >= minBookingPeriod;
    if (!isAtLeastMinBookingPeriod) {
      this.setState({ minBookingPeriodError: true });
    } else if (!startDate) {
      e.preventDefault();
      this.setState({ minBookingPeriodError: false });
      this.setState({ focusedInput: START_DATE });
    } else if (!endDate) {
      e.preventDefault();
      this.setState({ minBookingPeriodError: false });
      this.setState({ focusedInput: END_DATE });
    } else if (!seats) {
      e.preventDefault();
    } else {
      this.props.onSubmit(e);
    }
  }

  // When the values of the form are updated we need to fetch
  // lineItems from FTW backend for the EstimatedTransactionMaybe
  // In case you add more fields to the form, make sure you add
  // the values here to the bookingData object.
  handleOnChange(formValues) {
    const { startDate, endDate } =
      formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};
    const seats = formValues.values.seats;
    const selectedPaymentOption = formValues.values.selectedPaymentOption;
    const listingId = this.props.listingId;
    const isOwnListing = this.props.isOwnListing;
    const minBookingPeriod = this.props.minBookingPeriod;
    const bookingPeriodInDays = daysBetween(startDate, endDate);
    const isAtLeastMinBookingPeriod =
      typeof bookingPeriodInDays === 'number' && bookingPeriodInDays >= minBookingPeriod;
    if (startDate && endDate && isAtLeastMinBookingPeriod && !this.props.fetchLineItemsInProgress) {
      const timeSlots = this.props.timeSlots;
      //we find the maximum common amound of seats for all the days selected
      const maximumNumberOfSeatsAvailable = maximumCommonNumberOfSeats(
        startDate,
        endDate,
        timeSlots
      );

      const subscriptionLineItems = residualInterval(startDate, endDate);
      const residualMonth = subscriptionLineItems?.residualMonth;
      const subscriptionFirstMonth = subscriptionLineItems?.subscriptionFirstMonth;
      const monthCount = subscriptionLineItems?.monthCount;

      this.setState({ maximumNumberOfSeats: maximumNumberOfSeatsAvailable });
      this.setState({ minBookingPeriodError: false });
      if (maximumNumberOfSeatsAvailable >= seats) {
        this.props.onFetchTransactionLineItems({
          bookingData: {
            startDate,
            endDate,
            seats,
            residualMonth,
            subscriptionFirstMonth,
            monthCount,
            selectedPaymentOption,
          },
          listingId,
          isOwnListing,
        });
      }
    } else if (startDate && endDate && !isAtLeastMinBookingPeriod) {
      this.setState({ minBookingPeriodError: true });
    }
  }

  render() {
    const { rootClassName, className, price: unitPrice, ...rest } = this.props;
    const classes = classNames(rootClassName || css.root, className);

    if (!unitPrice) {
      return (
        <div className={classes}>
          <p className={css.error}>
            <FormattedMessage id="BookingDatesForm.listingPriceMissing" />
          </p>
        </div>
      );
    }
    if (unitPrice.currency !== config.currency) {
      return (
        <div className={classes}>
          <p className={css.error}>
            <FormattedMessage id="BookingDatesForm.listingCurrencyInvalid" />
          </p>
        </div>
      );
    }

    const listingAllowFullPaymentOption = this.props.listing?.attributes?.publicData?.listingAllowFullPaymentOption;
    const fullPaymentOptionOptional = !listingAllowFullPaymentOption || listingAllowFullPaymentOption === ListingFullPaymentOptionEnum.Optional;
    const fullPaymentOptionAllowed = !listingAllowFullPaymentOption || listingAllowFullPaymentOption === ListingFullPaymentOptionEnum.Yes || listingAllowFullPaymentOption === ListingFullPaymentOptionEnum.Optional;
    const defaultPaymentOption = fullPaymentOptionAllowed ? PaymentOptionEnum.FullPayment : PaymentOptionEnum.Monthly;

    return (
      <FinalForm
        {...rest}
        unitPrice={unitPrice}
        onSubmit={this.handleFormSubmit}
        initialValues={{ selectedPaymentOption: defaultPaymentOption }}
        render={fieldRenderProps => {
          const {
            endDatePlaceholder,
            startDatePlaceholder,
            form,
            formId,
            handleSubmit,
            intl,
            isOwnListing,
            submitButtonWrapperClassName,
            unitType,
            values,
            timeSlots,
            fetchTimeSlotsError,
            lineItems,
            fetchLineItemsInProgress,
            fetchLineItemsError,
            listing,
          } = fieldRenderProps;
          const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};

          const bookingStartLabel = intl.formatMessage({
            id: 'BookingDatesForm.bookingStartTitle',
          });
          const bookingEndLabel = intl.formatMessage({
            id: 'BookingDatesForm.bookingEndTitle',
          });
          const requiredMessage = intl.formatMessage({
            id: 'BookingDatesForm.requiredDate',
          });
          const startDateErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.invalidStartDate',
          });
          const endDateErrorMessage = intl.formatMessage({
            id: 'FieldDateRangeInput.invalidEndDate',
          });
          const timeSlotsError = fetchTimeSlotsError ? (
            <p className={css.sideBarError}>
              <FormattedMessage id="BookingDatesForm.timeSlotsError" />
            </p>
          ) : null;

          // This is the place to collect breakdown estimation data.
          // Note: lineItems are calculated and fetched from FTW backend
          // so we need to pass only booking data that is needed otherwise
          // If you have added new fields to the form that will affect to pricing,
          // you need to add the values to handleOnChange function
          const bookingData =
            startDate && endDate
              ? {
                unitType,
                startDate,
                endDate,
              }
              : null;

          const minBookingPeriodErrorMaybe = this.state.minBookingPeriodError ? (
            <span className={css.sideBarError}>
              <FormattedMessage
                id="BookingDatesForm.minBookingPeriodError"
                values={{ minBookingPeriod: this.props.minBookingPeriod }}
              />
            </span>
          ) : null;

          const showEstimatedBreakdown =
            bookingData &&
            lineItems &&
            !fetchLineItemsInProgress &&
            !fetchLineItemsError &&
            !this.state.minBookingPeriodError &&
            this.state.maximumNumberOfSeats &&
            values.seats &&
            this.state.maximumNumberOfSeats >= values.seats;

          const bookingInfoMaybe = showEstimatedBreakdown ? (
            <div className={css.priceBreakdownContainer}>
              <h3 className={css.priceBreakdownTitle}>
                <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
              </h3>
              <EstimatedBreakdownMaybe
                bookingData={bookingData}
                lineItems={lineItems}
                listing={listing}
              />
            </div>
          ) : null;

          const loadingSpinnerMaybe = fetchLineItemsInProgress ? (
            <IconSpinner className={css.spinner} />
          ) : null;

          const bookingInfoErrorMaybe = fetchLineItemsError ? (
            <span className={css.sideBarError}>
              <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
            </span>
          ) : null;

          const dateFormatOptions = {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          };

          const now = moment();
          const today = now.startOf('day').toDate();
          const tomorrow = now
            .startOf('day')
            .add(1, 'days')
            .toDate();
          const startDatePlaceholderText =
            startDatePlaceholder || intl.formatDate(today, dateFormatOptions);
          const endDatePlaceholderText =
            endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);
          const submitButtonClasses = classNames(
            submitButtonWrapperClassName || css.submitButtonWrapper
          );

          const defaultNumberOfSeats = listing?.attributes?.publicData?.availableSeats;
          const seatsMessage = (
            <FormattedMessage
              id="BookingDatesForm.seatsMessage"
              values={{ seatsLeft: this.state.maximumNumberOfSeats, defaultNumberOfSeats }}
            />
          );
          const seatsPlaceholderMessage = intl.formatMessage(
            {
              id: 'BookingDatesForm.seatsPlaceholderMessage',
            },
            {
              maximumNumberOfSeats: this.state.maximumNumberOfSeats,
            }
          );
          const seatsRequiredMessage = (
            <FormattedMessage
              id="BookingDatesForm.seatsRequiredMessage"
              values={{ maximumNumberOfSeats: this.state.maximumNumberOfSeats }}
            />
          );

          const bookingQtyAtleast1Month = getBookingQtyAtleast1Month(startDate, endDate)

          const listingFullPaymentOptionDiscount = listing?.attributes?.publicData?.listingFullPaymentOptionDiscount;
          const fullPaymentOptionLabel = fullPaymentOptionAllowed && listingFullPaymentOptionDiscount ? intl.formatMessage({ id: "BookingDatesForm.fullPaymentOptionWithDiscount" }, { listingFullPaymentOptionDiscount }) :
            intl.formatMessage({ id: "BookingDatesForm.fullPaymentOptionNoDiscount" }, { listingFullPaymentOptionDiscount })

          return (
            <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
              {timeSlotsError}
              <FormSpy
                subscription={{ values: true }}
                onChange={values => {
                  this.handleOnChange(values);
                }}
              />
              <FieldDateRangeInput
                className={css.bookingDates}
                name="bookingDates"
                unitType={unitType}
                startDateId={`${formId}.bookingStartDate`}
                startDateLabel={bookingStartLabel}
                startDatePlaceholderText={startDatePlaceholderText}
                endDateId={`${formId}.bookingEndDate`}
                endDateLabel={bookingEndLabel}
                endDatePlaceholderText={endDatePlaceholderText}
                focusedInput={this.state.focusedInput}
                onFocusedInputChange={this.onFocusedInputChange}
                format={identity}
                timeSlots={timeSlots}
                useMobileMargins
                validate={composeValidators(
                  required(requiredMessage),
                  bookingDatesRequired(startDateErrorMessage, endDateErrorMessage)
                )}
                disabled={fetchLineItemsInProgress}
                listing={listing}
              />

              {this.state.maximumNumberOfSeats && (
                <FieldTextInput
                  id="seats"
                  name="seats"
                  className={css.textField}
                  type="number"
                  label={seatsMessage}
                  min={1}
                  max={this.state.maximumNumberOfSeats}
                  placeholder={seatsPlaceholderMessage}
                  validate={numberBetween(seatsRequiredMessage, 1, this.state.maximumNumberOfSeats)}
                />
              )}

              {startDate && endDate && values.seats && bookingQtyAtleast1Month && fullPaymentOptionOptional &&
                <div className={css.paymentOptionFields}>
                  <label><FormattedMessage id="BookingDatesForm.paymentOption.label" /></label>
                  <FieldRadioButton
                    id="fullPaymentOption"
                    name="selectedPaymentOption"
                    label={fullPaymentOptionLabel}
                    value={PaymentOptionEnum.FullPayment}
                  />
                  <FieldRadioButton
                    id="subscriptionOption"
                    name="selectedPaymentOption"
                    label={intl.formatMessage({ id: "BookingDatesForm.monthlyOption" })}
                    value={PaymentOptionEnum.Monthly}
                  />
                </div>
              }

              {bookingInfoMaybe}
              {loadingSpinnerMaybe}
              {bookingInfoErrorMaybe}
              {minBookingPeriodErrorMaybe}
              <p className={css.smallPrint}>
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingDatesForm.ownListing'
                      : 'BookingDatesForm.youWontBeChargedInfo'
                  }
                />
              </p>
              <div className={submitButtonClasses}>
                <PrimaryButton type="submit">
                  <FormattedMessage id="BookingDatesForm.requestToBook" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    );
  }
}

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  submitButtonWrapperClassName: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  timeSlots: null,
  lineItems: null,
  fetchLineItemsError: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  submitButtonWrapperClassName: string,

  unitType: propTypes.bookingUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  timeSlots: arrayOf(propTypes.timeSlot),

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,

  // from injectIntl
  intl: intlShape.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
